import React from 'react';
import {
  LayoutDashboard,
  FileText,
  FlaskConical,
  Files,
  LayoutTemplate,
  Settings,
  Sparkles,
  X,
} from 'lucide-react';
import { UNIVERSITY_CONFIG } from '../../config/university.config';

export type NavRoute = 'dashboard' | 'cover-studio' | 'report-studio' | 'document-studio' | 'templates' | 'settings';

interface SidebarProps {
  currentRoute: NavRoute;
  onRouteChange: (route: NavRoute) => void;
  isOpen: boolean;
  onClose?: () => void;
  onCloseMobile?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  currentRoute,
  onRouteChange,
  isOpen,
  onClose,
  onCloseMobile,
}) => {
  const navItems: Array<{ id: NavRoute; label: string; icon: React.ReactNode; badge?: string }> = [
    { id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard size={18} /> },
    { id: 'cover-studio', label: 'Cover Studio', icon: <FileText size={18} /> },
    { id: 'report-studio', label: 'Report Studio', icon: <FlaskConical size={18} />, badge: 'Primary' },
    { id: 'document-studio', label: 'Document Studio', icon: <Files size={18} /> },
    { id: 'templates', label: 'Templates', icon: <LayoutTemplate size={18} /> },
    { id: 'settings', label: 'Settings', icon: <Settings size={18} /> },
  ];

  const handleClose = () => {
    if (onClose) onClose();
    else if (onCloseMobile) onCloseMobile();
  };

  const handleNavClick = (route: NavRoute) => {
    onRouteChange(route);
    handleClose();
  };

  return (
    <aside className={`app-sidebar ${isOpen ? 'open' : ''}`}>
      {/* Brand Header */}
      <div className="sidebar-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 'var(--radius-md)',
              backgroundColor: 'var(--primary-600)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
            }}
          >
            <Sparkles size={18} />
          </div>
          <div className="sidebar-brand-text">
            <span className="sidebar-app-name">Student Studio</span>
            <span className="sidebar-app-subtitle">Academic Suite</span>
          </div>
        </div>

        {(onClose || onCloseMobile) && (
          <button
            type="button"
            className="icon-btn"
            style={{ width: 28, height: 28 }}
            onClick={handleClose}
            aria-label="Close navigation menu"
            title="Close navigation"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Navigation Links */}
      <nav className="sidebar-nav" aria-label="Main Navigation">
        <div className="nav-section-title">Workspaces</div>
        {navItems.map((item) => {
          const isActive = currentRoute === item.id;
          return (
            <button
              key={item.id}
              className={`nav-item ${isActive ? 'active' : ''}`}
              onClick={() => handleNavClick(item.id)}
              aria-current={isActive ? 'page' : undefined}
            >
              {item.icon}
              <span style={{ flex: 1, textAlign: 'left' }}>{item.label}</span>
              {item.badge && (
                <span
                  style={{
                    fontSize: '10px',
                    fontWeight: 700,
                    padding: '2px 6px',
                    borderRadius: 'var(--radius-full)',
                    backgroundColor: isActive ? 'var(--primary-200)' : 'var(--bg-surface-hover)',
                    color: isActive ? 'var(--primary-800)' : 'var(--text-secondary)',
                    textTransform: 'uppercase',
                  }}
                >
                  {item.badge}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Institutional Branding Footer */}
      <div className="sidebar-footer">
        <div className="institution-badge" title={`${UNIVERSITY_CONFIG.name} (${UNIVERSITY_CONFIG.shortName})`}>
          <img
            src={UNIVERSITY_CONFIG.logoPath}
            alt="University Logo"
            className="institution-badge-logo"
            onError={(e) => {
              // Graceful fallback if image is missing
              (e.target as HTMLElement).style.display = 'none';
            }}
          />
          <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <span style={{ fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
              {UNIVERSITY_CONFIG.shortName}
            </span>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
              {UNIVERSITY_CONFIG.name}
            </span>
          </div>
        </div>
      </div>
    </aside>
  );
};
